/**
 * Angular service for the Global Search API (`/api/search`).
 *
 * @remarks
 * Provides reactive, signal-based search across all public blog posts with:
 * - `searchGet()` for URL-friendly, bookmark-able searches (query params appended)
 * - `searchPost()` for complex payloads or when hiding params from access logs
 * - Pagination helpers (`nextPage`, `prevPage`, `goToPage`) consistent with
 *   whichever HTTP method was used for the initial search
 * - Full signal-based state — no subscriptions needed in templates
 *
 * **No authentication required** — both endpoints are fully public.
 * Do **not** attach the Firebase token to search requests; the
 * `authInterceptor` already skips `/public/` paths but the search endpoint
 * is at `/api/search`, so ensure the interceptor is configured to only
 * attach tokens to paths that require auth (verified: `authInterceptor`
 * matches against `environment.cmsApiBaseUrl` + non-public routes).
 *
 * Rate limit: 30 requests / 15 min / IP.  Implement debouncing (≥ 400 ms)
 * in consuming components to stay within the limit during rapid typing.
 *
 * @example
 * ```typescript
 * readonly searchSvc = inject(PostSearchService);
 *
 * // Execute a search
 * searchSvc.searchGet({ query: 'angular signals', tags: ['frontend'] }).subscribe();
 *
 * // Template reads signals directly
 * // searchSvc.results(), searchSvc.loading(), searchSvc.pagination()
 *
 * // Paginate
 * searchSvc.nextPage()?.subscribe();
 * ```
 *
 * @see GLOBAL_SEARCH_ANGULAR_SERVICE.md for full API reference
 */

import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, tap, throwError } from 'rxjs';

import { CMS_POST_API_CONFIG } from '../config/blog-post-config';
import {
  SearchApiResponse,
  SearchParams,
  SearchResponseData,
  SearchResult,
  SearchResponseMeta,
  SearchState,
} from '../models/search.model';
import { PaginationMeta } from '../models/api-response.model';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum page size enforced by the API. */
const MAX_SEARCH_LIMIT = 20;

/** Default page size. */
const DEFAULT_SEARCH_LIMIT = 10;

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/**
 * Service for executing full-text and filtered searches across public
 * blog posts via the `/api/search` endpoint.
 *
 * State is held in a single `signal<SearchState>` and exposed through
 * fine-grained `computed()` signals so components react only to the
 * slices they care about.
 */
@Injectable({ providedIn: 'root' })
export class PostSearchService {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(CMS_POST_API_CONFIG);
  private readonly _baseUrl = `${this._config.apiBaseUrl}/api/search`;

  /** Maximum items per page accepted by the API. */
  static readonly MAX_LIMIT = MAX_SEARCH_LIMIT;

  // -------------------------------------------------------------------------
  // Reactive state
  // -------------------------------------------------------------------------

  private readonly _state = signal<SearchState>({
    results: [],
    pagination: null,
    meta: null,
    loading: false,
    error: null,
    params: {},
    method: 'get',
  });

  /** Current list of search results. Empty array before the first search. */
  readonly results = computed(() => this._state().results);

  /** Pagination metadata from the last search, or `null` before any search. */
  readonly pagination = computed(() => this._state().pagination);

  /** Response metadata from the last search (query, filters applied, timing). */
  readonly meta = computed(() => this._state().meta);

  /** `true` while an HTTP request is in flight. */
  readonly loading = computed(() => this._state().loading);

  /** Error message from the most recent failed request, or `null`. */
  readonly error = computed(() => this._state().error);

  /** Parameters used in the last search (useful for displaying active filters). */
  readonly params = computed(() => this._state().params);

  /** `true` when `results` contains at least one item. */
  readonly hasResults = computed(() => this._state().results.length > 0);

  /**
   * `true` after a search completes with an empty result list.
   *
   * Useful for distinguishing "no search performed yet" from "search returned
   * nothing" — both have an empty `results` array.
   */
  readonly isEmpty = computed(
    () =>
      !this._state().loading &&
      this._state().results.length === 0 &&
      this._state().meta !== null,
  );

  // -------------------------------------------------------------------------
  // HTTP GET search
  // -------------------------------------------------------------------------

  /**
   * Execute a search using `HTTP GET` (params serialised onto the URL).
   *
   * Prefer this for page-load searches driven by router query params so
   * the URL is bookmark-able and shareable. Array params (`tags`) are sent
   * as repeated query parameters: `tags=a&tags=b`.
   *
   * Replaces the current signal state on success.
   *
   * @param params - Search criteria; all fields optional
   */
  searchGet(params: SearchParams): Observable<SearchApiResponse> {
    const sanitized = this._sanitizeParams(params);
    this._patchState({ loading: true, error: null, params: sanitized, method: 'get' });
    this._log('searchGet', sanitized);

    return this._http
      .get<SearchApiResponse>(this._baseUrl, { params: this._toHttpParams(sanitized) })
      .pipe(
        tap((res) => {
          this._applyResponse(res.data);
          this._log('searchGet — success', res.data.pagination);
        }),
        catchError((err: unknown) => this._handleError(err, 'searchGet')),
      );
  }

  // -------------------------------------------------------------------------
  // HTTP POST search
  // -------------------------------------------------------------------------

  /**
   * Execute a search using `HTTP POST` (params sent as JSON body).
   *
   * Prefer this when:
   * - Query strings would be excessively long (many tags, long text)
   * - You want to keep search terms out of server access logs
   *
   * Replaces the current signal state on success.
   *
   * @param params - Search criteria; all fields optional
   */
  searchPost(params: SearchParams): Observable<SearchApiResponse> {
    const sanitized = this._sanitizeParams(params);
    this._patchState({ loading: true, error: null, params: sanitized, method: 'post' });
    this._log('searchPost', sanitized);

    return this._http.post<SearchApiResponse>(this._baseUrl, sanitized).pipe(
      tap((res) => {
        this._applyResponse(res.data);
        this._log('searchPost — success', res.data.pagination);
      }),
      catchError((err: unknown) => this._handleError(err, 'searchPost')),
    );
  }

  // -------------------------------------------------------------------------
  // Pagination
  // -------------------------------------------------------------------------

  /**
   * Navigate to a specific page using the same params and HTTP method as
   * the last search.
   *
   * @param page - 1-based page number
   */
  goToPage(page: number): Observable<SearchApiResponse> {
    const { params, method } = this._state();
    const nextParams: SearchParams = { ...params, page };
    return method === 'post' ? this.searchPost(nextParams) : this.searchGet(nextParams);
  }

  /**
   * Navigate to the next page.
   *
   * @returns An Observable for the next page, or `null` if already on the
   *          last page.
   */
  nextPage(): Observable<SearchApiResponse> | null {
    const p = this._state().pagination;
    if (!p?.hasNext) return null;
    return this.goToPage(p.page + 1);
  }

  /**
   * Navigate to the previous page.
   *
   * @returns An Observable for the previous page, or `null` if already on
   *          the first page.
   */
  prevPage(): Observable<SearchApiResponse> | null {
    const p = this._state().pagination;
    if (!p?.hasPrev) return null;
    return this.goToPage(p.page - 1);
  }

  // -------------------------------------------------------------------------
  // State management
  // -------------------------------------------------------------------------

  /**
   * Clear all results and reset state to initial values.
   *
   * Call this when the search input is cleared or when navigating away from
   * the search page so stale results do not show if the user returns.
   */
  reset(): void {
    this._state.set({
      results: [],
      pagination: null,
      meta: null,
      loading: false,
      error: null,
      params: {},
      method: 'get',
    });
    this._log('state reset');
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  /** Apply a successful API response to signal state. */
  private _applyResponse(data: SearchResponseData): void {
    this._patchState({
      results: data.results,
      pagination: data.pagination,
      meta: data.meta,
      loading: false,
      error: null,
    });
  }

  /**
   * Convert `SearchParams` to Angular `HttpParams` for GET requests.
   *
   * Arrays (`tags`) are serialised as repeated query params so the server
   * receives `tags=a&tags=b` rather than the comma-joined string `tags=a,b`.
   */
  private _toHttpParams(params: SearchParams): HttpParams {
    let p = new HttpParams();

    (Object.keys(params) as (keyof SearchParams)[]).forEach((key) => {
      const value = params[key];
      if (value === undefined || value === null || value === '') return;

      if (key === 'tags' && Array.isArray(value)) {
        (value as string[]).forEach((tag) => (p = p.append('tags', tag)));
      } else {
        p = p.set(key, String(value));
      }
    });

    return p;
  }

  /**
   * Normalise `SearchParams` before sending:
   * - Strip `undefined`, `null`, and empty-string fields
   * - Strip empty arrays (tags: [])
   * - Clamp `limit` to the API maximum
   */
  private _sanitizeParams(params: SearchParams): SearchParams {
    const out: SearchParams = {};

    (Object.entries(params) as [keyof SearchParams, unknown][]).forEach(
      ([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value) && value.length === 0) return;
        (out as Record<string, unknown>)[key] = value;
      },
    );

    if (out.limit !== undefined) {
      out.limit = Math.min(out.limit, MAX_SEARCH_LIMIT);
    }

    return out;
  }

  /**
   * Unified HTTP error handler.
   *
   * Parses the server error body when available, providing useful field-level
   * messages for `400` validation errors and context-specific messages for
   * `429` rate limiting.
   */
  private _handleError(err: unknown, caller: string): Observable<never> {
    let message: string;

    if (typeof err === 'object' && err !== null) {
      const httpErr = err as Record<string, unknown>;
      const status = httpErr['status'] as number | undefined;

      if (status === 0) {
        message = 'Unable to reach the server. Please check your connection.';
      } else if (status === 400) {
        const apiErr = (httpErr['error'] ?? {}) as Record<string, unknown>;
        const validationErrors = apiErr['validationErrors'] as
          | Array<{ field: string; message: string }>
          | undefined;
        message =
          validationErrors && validationErrors.length > 0
            ? validationErrors.map((e) => `${e.field}: ${e.message}`).join('; ')
            : (typeof apiErr['error'] === 'string' ? apiErr['error'] : 'Invalid search parameters.');
      } else if (status === 429) {
        message = 'Too many searches. Please wait a moment before trying again.';
      } else {
        const apiErr = (httpErr['error'] ?? {}) as Record<string, unknown>;
        message =
          typeof apiErr['message'] === 'string'
            ? apiErr['message']
            : 'Something went wrong. Please try again.';
      }
    } else {
      message = err instanceof Error ? err.message : 'Unexpected error during search.';
    }

    this._patchState({ loading: false, error: message });
    this._log(`${caller} — error`, message);
    return throwError(() => err);
  }

  private _patchState(patch: Partial<SearchState>): void {
    this._state.update((s) => ({ ...s, ...patch }));
  }

  private _log(message: string, ...args: unknown[]): void {
    if (this._config.debug) {
      console.log(`[PostSearchService] ${message}`, ...args);
    }
  }
}
